import { getMidwayToken, clearToken, redirectToMidway } from '../auth/midway';
import { getSettings } from '../config';

/**
 * Build request headers, attaching the Midway Bearer token only when one is
 * available (never a placeholder/fallback token — see ticket V2306137056).
 */
async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await getMidwayToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * If the API rejects us as unauthenticated, the token is missing/expired/invalid.
 * Clear it and bounce through Midway to re-authenticate.
 */
function handleUnauthorized(): void {
  clearToken();
  redirectToMidway();
}

export async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const { apiUrl } = await getSettings();
  const url = new URL(`${apiUrl}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v) url.searchParams.set(k, v);
    });
  }

  const res = await fetch(url.toString(), { headers: await buildHeaders() });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized — redirecting to Midway');
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

export async function apiPost<T>(path: string, body?: any): Promise<T> {
  const { apiUrl } = await getSettings();
  const res = await fetch(`${apiUrl}${path}`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized — redirecting to Midway');
  }
  if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function apiPut<T>(path: string, body: any): Promise<T> {
  const { apiUrl } = await getSettings();
  const res = await fetch(`${apiUrl}${path}`, {
    method: 'PUT',
    headers: await buildHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('Unauthorized — redirecting to Midway');
  }
  if (!res.ok) throw new Error(`API error: ${res.status} ${res.statusText}`);
  return res.json();
}
